import type { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { findDisplayedVersion } from 'shared/domain/documentation/documentationVersionHelpers';
import type { HashMap } from 'shared/types/commonView';
import type { LinkedDocumentationItemInDto } from 'shared/dtos/in/issue';
import type { DocumentationSignedUrls } from 'shared/domain/documentation/documentationContract';
import { pickFirstNonEmptyString } from 'shared/utils/array';
import { normalizeEntityIdentifier } from 'shared/utils/id/normalizeEntityIdentifier';
import type {
  CreateLinkedDocumentationItemParams,
  LinkedDescriptionMap,
  LinkedDocumentationItem,
  LinkedDocumentationReference,
  LinkedDocumentationResourceMeta,
  LinkedDocPreview,
  MaybeNumber,
  RequestableLinkedDocumentationItem,
} from './types';
import {
  isDocumentationLinkType,
  isVersionLinkTarget,
  LinkedDocumentationTarget,
  LinkedDocumentationType,
  ResourceLoadStatus,
} from './types';

export const EMPTY_PREVIEW: LinkedDocPreview = { url: '' };
export const ERROR_PREVIEW: LinkedDocPreview = { url: '#' };

export const LINKED_DOCUMENTATION_CACHE_POLICY = {
  previewTtlMs: 10 * 60 * 1000,
  previewMaxEntries: 200,
  detailsTtlMs: 15 * 60 * 1000,
  detailsMaxEntries: 200,
  retryDelaysMs: [1_000, 3_000, 7_000, 15_000],
} as const;

export function getLinkedDocumentationRetryDelayMs(retryCount: number): number {
  const delays = LINKED_DOCUMENTATION_CACHE_POLICY.retryDelaysMs;
  const lastIndex = delays.length - 1;
  return delays[Math.min(Math.max(retryCount, 0), lastIndex)];
}

export function isUsableLinkedDocumentationResource(
  meta: LinkedDocumentationResourceMeta | undefined,
  now: number
): boolean {
  if (meta?.status !== ResourceLoadStatus.Ready) {
    return false;
  }

  return meta.expiresAt == null || meta.expiresAt > now;
}

export function canRetryLinkedDocumentationResource(
  meta: LinkedDocumentationResourceMeta | undefined,
  now: number
): boolean {
  if (meta?.status !== ResourceLoadStatus.Error) {
    return false;
  }

  return meta.nextRetryAt == null || meta.nextRetryAt <= now;
}

export function isExpiredLinkedDocumentationResource(
  meta: LinkedDocumentationResourceMeta | undefined,
  now: number
): boolean {
  if (meta?.status !== ResourceLoadStatus.Ready) {
    return false;
  }

  return meta.expiresAt != null && meta.expiresAt <= now;
}

export function shouldStartLinkedDocumentationResourceRequest(
  meta: LinkedDocumentationResourceMeta | undefined,
  now: number
): boolean {
  if (!meta || meta.status === ResourceLoadStatus.Idle) {
    return true;
  }

  if (canRetryLinkedDocumentationResource(meta, now)) {
    return true;
  }

  return isExpiredLinkedDocumentationResource(meta, now);
}

function parseAwsDateToMs(value: string): number | undefined {
  if (!/^\d{8}T\d{6}Z$/.test(value)) {
    return undefined;
  }

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6)) - 1;
  const day = Number(value.slice(6, 8));
  const hour = Number(value.slice(9, 11));
  const minute = Number(value.slice(11, 13));
  const second = Number(value.slice(13, 15));

  const timestamp = Date.UTC(year, month, day, hour, minute, second);
  return Number.isFinite(timestamp) ? timestamp : undefined;
}

export function resolveLinkedDocumentationPreviewExpiryMs(
  preview: LinkedDocPreview,
  now = Date.now()
): number {
  const fallbackExpiry = now + LINKED_DOCUMENTATION_CACHE_POLICY.previewTtlMs;
  const urlCandidate =
    preview.url || preview.imageSignedRequest || preview.thumbnailSignedRequest;

  if (!urlCandidate) {
    return fallbackExpiry;
  }

  try {
    const parsedUrl = new URL(urlCandidate);
    const expires = Number(
      parsedUrl.searchParams.get('X-Amz-Expires') ||
        parsedUrl.searchParams.get('x-amz-expires')
    );
    const dateRaw =
      parsedUrl.searchParams.get('X-Amz-Date') ||
      parsedUrl.searchParams.get('x-amz-date');
    const startAt = dateRaw ? parseAwsDateToMs(dateRaw) : undefined;

    if (Number.isFinite(expires) && startAt) {
      return startAt + expires * 1000;
    }
  } catch {
    return fallbackExpiry;
  }

  return fallbackExpiry;
}

/** The preview of a linked documentation version from its signed URLs. */
export function documentationSignedUrlsToPreview(urls: DocumentationSignedUrls): LinkedDocPreview {
  const { signedRequest, imageRotation, ...optionalUrls } = urls;
  return {
    url: signedRequest,
    ...optionalUrls,
    ...(imageRotation !== undefined ? { imageRotation } : {}),
    viaServiceWorker: true,
  };
}

export function getRequestableLinkedDocumentationItems(
  items: LinkedDocumentationItem[]
): RequestableLinkedDocumentationItem[] {
  return items.filter(
    (item): item is RequestableLinkedDocumentationItem =>
      Boolean(item.documentationId && item.versionId != null)
  );
}

function normalizeFiniteNumber(value: MaybeNumber): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

export function resolveLinkedDocumentationImageRotation(
  candidates: MaybeNumber[]
): number | undefined {
  for (const candidate of candidates) {
    const normalized = normalizeFiniteNumber(candidate);
    if (normalized != null) return normalized;
  }

  return undefined;
}

export function createLinkedDocumentationItem({
  type,
  documentationId,
  versionId,
  sourceDocumentId,
  nameCandidates,
  descriptionCandidates,
  imageRotationCandidates,
}: CreateLinkedDocumentationItemParams): LinkedDocumentationItem {
  const name = pickFirstNonEmptyString(nameCandidates, documentationId);
  const description = pickFirstNonEmptyString(descriptionCandidates ?? []);

  return {
    name,
    ...(description ? { description } : {}),
    type,
    documentationId,
    versionId,
    ...(sourceDocumentId ? { sourceDocumentId } : {}),
    imageRotation: resolveLinkedDocumentationImageRotation(
      imageRotationCandidates ?? []
    ),
    deleted: false,
  };
}

/**
 * A link to a documentation shows its current version (PT-7117): once the
 * documentation's details are known the link carries that version, so the
 * previews, tiles and their loading treat every link alike. Its key stays the
 * documentation's, whichever version is current.
 */
export function withShownVersions(
  items: LinkedDocumentationItem[],
  documentationsById: HashMap<DocumentationModel>
): LinkedDocumentationItem[] {
  return items.map((item) => {
    const documentation = item.documentationId ? documentationsById[item.documentationId] : undefined;
    if (!documentation || !isDocumentationLink(item)) {
      return item;
    }
    const shown = findDisplayedVersion(documentation);
    return shown ? { ...item, versionId: shown._id } : item;
  });
}

/** Whether the issue links to the target already, as the backend's duplicate check sees it (same type, documentation and version). */
export function isAlreadyLinked(
  linkedDocumentation: LinkedDocumentationItem[],
  target: LinkedDocumentationTarget
): boolean {
  const targetKey = getLinkedDocumentationTargetKey(target);
  return linkedDocumentation.some((item) => !item.deleted && getLinkedDocumentationItemKey(item) === targetKey);
}

/** The type of a link; a link stored without one points at its version, as the backend reads it. */
export function getLinkedDocumentationType(item: LinkedDocumentationItem): LinkedDocumentationType {
  return item.type ?? LinkedDocumentationType.version;
}

/** Whether the link points at a documentation, shown in its current version, rather than at one of its versions. */
export function isDocumentationLink(item: LinkedDocumentationItem): boolean {
  return isDocumentationLinkType(getLinkedDocumentationType(item));
}

/** What the link points at; `undefined` while it lacks an id its type needs. */
export function toLinkedDocumentationTarget(item: LinkedDocumentationItem): LinkedDocumentationTarget | undefined {
  const { documentationId, versionId } = item;
  if (!documentationId) {
    return undefined;
  }
  if (isDocumentationLink(item)) {
    return { type: LinkedDocumentationType.documentation, documentationId };
  }
  return versionId ? { type: LinkedDocumentationType.version, documentationId, versionId } : undefined;
}

/**
 * What tells two links apart, as the backend's duplicate check does: the type,
 * the documentation and, for a link to a version, the version. A link to a
 * documentation has no version, so it is keyed by the documentation alone.
 */
export function getLinkedDocumentationItemKey(item: LinkedDocumentationItem): string | undefined {
  const target = toLinkedDocumentationTarget(item);
  return target ? getLinkedDocumentationTargetKey(target) : undefined;
}

export function getLinkedDocumentationTargetKey(target: LinkedDocumentationTarget): string {
  return isVersionLinkTarget(target)
    ? `${target.type}:${target.documentationId}:${target.versionId}`
    : `${target.type}:${target.documentationId}`;
}

/**
 * Keeps server-linked items and preserves local-only links until the next sync
 * includes them (e.g. right after export-to-documentation).
 */
export function mergeLinkedDocumentationItems(
  localItems: LinkedDocumentationItem[] | undefined,
  remoteItems: LinkedDocumentationItem[] | undefined
): LinkedDocumentationItem[] {
  const mergedByKey = new Map<string, LinkedDocumentationItem>();

  (remoteItems ?? [])
    .filter((item) => !item.deleted)
    .forEach((item) => {
      const key = getLinkedDocumentationItemKey(item);
      if (key) {
        mergedByKey.set(key, item);
      }
    });

  (localItems ?? [])
    .filter((item) => !item.deleted)
    .forEach((item) => {
      const key = getLinkedDocumentationItemKey(item);
      if (key && !mergedByKey.has(key)) {
        mergedByKey.set(key, item);
      }
    });

  return Array.from(mergedByKey.values());
}

export function doesLinkedDocumentationMatchReference(
  item: LinkedDocumentationItem,
  reference: LinkedDocumentationReference
): boolean {
  if (
    reference.linkedDocumentationId &&
    item._id === reference.linkedDocumentationId
  ) {
    return true;
  }

  if (!reference.documentationId || !reference.versionId) {
    return false;
  }

  return (
    item.documentationId === reference.documentationId &&
    item.versionId === reference.versionId
  );
}

export function findLinkedDocumentationByReference(
  linkedDocumentation: LinkedDocumentationItem[],
  reference: LinkedDocumentationReference
): LinkedDocumentationItem | undefined {
  return linkedDocumentation.find((item) =>
    doesLinkedDocumentationMatchReference(item, reference)
  );
}

export function resolveLinkedDocumentationIdentifier(
  linkedDocumentation: LinkedDocumentationItem[],
  reference: LinkedDocumentationReference
): string | undefined {
  const matchedItem = findLinkedDocumentationByReference(
    linkedDocumentation,
    reference
  );

  return matchedItem?._id ?? reference.linkedDocumentationId;
}

export function linkedDocumentationItemInDtoToItem(
  dto: LinkedDocumentationItemInDto,
  descriptionBySourceDocumentId?: LinkedDescriptionMap
): LinkedDocumentationItem {
  const normalizedId =
    normalizeEntityIdentifier(dto._id) ?? normalizeEntityIdentifier(dto.id);
  const normalizedDocumentationId = normalizeEntityIdentifier(
    dto.documentationId
  );
  const normalizedVersionId = normalizeEntityIdentifier(dto.versionId);
  const normalizedSourceDocumentId = normalizeEntityIdentifier(
    dto.sourceDocumentId
  );

  const name = pickFirstNonEmptyString([
    dto.name,
    dto.documentationName,
    dto.label,
  ]);

  const descriptionFromSourceDocument =
    dto.sourceDocumentId != null
      ? descriptionBySourceDocumentId?.[String(dto.sourceDocumentId)]
      : undefined;

  const resolvedDescription = String(
    dto.description ?? descriptionFromSourceDocument ?? ''
  ).trim();

  return {
    name,
    ...(resolvedDescription ? { description: resolvedDescription } : {}),
    _id: normalizedId,
    ...(dto.type ? { type: dto.type } : {}),
    documentationId: normalizedDocumentationId,
    versionId: normalizedVersionId,
    imageRotation: dto.imageRotation,
    sourceDocumentId: normalizedSourceDocumentId,
    createdAt: dto.createdAt,
    createdBy: normalizeEntityIdentifier(dto.createdBy),
    documentationName: dto.documentationName,
    label: dto.label,
    deleted: dto.deleted ?? false,
  };
}

export function linkedDocumentationInDtoToItems(
  dtos: LinkedDocumentationItemInDto[] | undefined,
  descriptionBySourceDocumentId?: LinkedDescriptionMap
): LinkedDocumentationItem[] {
  return (dtos ?? [])
    .filter((dto) => !dto.deleted)
    .map((dto) =>
      linkedDocumentationItemInDtoToItem(dto, descriptionBySourceDocumentId)
    );
}
